import {
  StalenessStatus,
  type StudyRunCaseDashboardMissionDto,
  type StudyRunCaseDashboardSelectDto,
  type StudyRunCaseDashboardThreadDetailDto,
  type StudyRunCaseDashboardThreadVerdictDto,
  type StudyRunCaseSelectDto,
} from '@@types/api.types';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { type MeiaScoringConfigFormValues } from '@schemas/meia.schema';
import { type FC } from 'react';
import { AdvisoryGapsPanel } from './meia-p4-advisory-gaps-panel.component';
import { MeasurementMatrixPanel } from './meia-p4-measurement-matrix.component';
import { PerformancePanel } from './meia-p4-performance-panel.component';
import { MissionScopePicker, ThreadVerdictPicker } from './meia-p4-pickers.component';
import { StageHeatmap } from './meia-p4-stage-heatmap.component';

export type RunCaseDashboardView = 'matrix' | 'runcases';

type RunCaseDashboardProps = {
  activeMission?: StudyRunCaseDashboardMissionDto | null;
  activeThread?: StudyRunCaseDashboardThreadDetailDto | null;
  activeThreadVerdict?: StudyRunCaseDashboardThreadVerdictDto | null;
  canEdit?: boolean;
  dashboard?: StudyRunCaseDashboardSelectDto;
  isLoading: boolean;
  isMissionScoped?: boolean;
  onActiveMissionChange: (missionId: string) => void;
  onActiveThreadChange: (threadId: string) => void;
  runCase?: StudyRunCaseSelectDto;
  scoringConfig: MeiaScoringConfigFormValues;
  showThreadTabs?: boolean;
  view?: RunCaseDashboardView;
};

export const RunCaseDashboard: FC<RunCaseDashboardProps> = (props) => {
  const {
    activeMission,
    activeThread,
    activeThreadVerdict,
    canEdit = false,
    dashboard,
    isLoading,
    isMissionScoped = false,
    onActiveMissionChange,
    onActiveThreadChange,
    runCase,
    scoringConfig,
    showThreadTabs = true,
    view = 'runcases',
  } = props;
  const isMatrixView = view === 'matrix';

  if (!runCase) {
    return (
      <Tile
        emptyState={{ message: 'Select a run case to inspect scoring output.', title: 'No run case selected' }}
        isEmpty
      >
        <span />
      </Tile>
    );
  }

  if (isLoading) {
    return (
      <Tile
        header={{ title: 'Run Case Dashboard' }}
        isLoading
      />
    );
  }

  if (!dashboard) {
    return (
      <Tile
        emptyState={{
          message: 'Select or run a case to inspect scoring output.',
          title: 'No dashboard data',
        }}
        isEmpty
      >
        <span />
      </Tile>
    );
  }

  const noScoreReason = getThreadNoScoreReason({ activeThread, activeThreadVerdict });
  if (isMissionScoped && !activeMission) {
    return (
      <EmptyStateContainer
        className="rounded-xl bg-white p-8 shadow-sm"
        message="Run a case after authoring this mission to inspect scoring output here."
        title="No scoring data for this mission"
      />
    );
  }

  const threadVerdicts = activeMission?.threadVerdicts ?? dashboard.threadVerdicts;
  const matrixRows = activeThread?.matrixRows ?? [];
  const advisoryGaps = activeThread?.advisoryGaps ?? [];
  const isStaleOutput = dashboard.scoringOutput?.stalenessStatus === StalenessStatus.STALE;

  return (
    <>
      {isStaleOutput ? (
        <div className="flex flex-col gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-semibold">Scoring results are stale.</div>
            <div className="text-xs text-yellow-800">
              Authoring changed after this run. Review the last results read-only, then rerun the epoch.
            </div>
          </div>
          <Badge variant="warning">Stale</Badge>
        </div>
      ) : null}
      {!isMissionScoped && dashboard.missions.length > 1 && (
        <MissionScopePicker
          activeMissionId={activeMission?.missionId ?? null}
          canEdit={canEdit}
          missions={dashboard.missions}
          onChange={onActiveMissionChange}
          studyId={runCase.studyId}
        />
      )}
      {showThreadTabs && (
        <ThreadVerdictPicker
          activeThreadId={activeThread?.threadId ?? null}
          canEdit={canEdit}
          missionId={activeMission?.missionId}
          onChange={onActiveThreadChange}
          studyId={runCase.studyId}
          threads={threadVerdicts}
        />
      )}
      {isMatrixView ? (
        <>
          <MeasurementMatrixPanel rows={matrixRows} />
          <AdvisoryGapsPanel gaps={advisoryGaps} />
        </>
      ) : dashboard.scoringOutput ? (
        noScoreReason ? (
          <EmptyStateContainer
            className="rounded-xl bg-white p-8 shadow-sm"
            message={noScoreReason}
            title="No Score"
          />
        ) : (
          <>
            <StageHeatmap
              rows={activeThread?.stageHeatmap ?? dashboard.stageHeatmap}
              scoringConfig={scoringConfig}
              summary={activeThread?.summary ?? dashboard.summary}
            />
            <PerformancePanel stages={activeThread?.performanceStages ?? dashboard.performanceStages} />
          </>
        )
      ) : (
        <EmptyStateContainer
          className="rounded-xl bg-white p-8 shadow-sm"
          message="Run an epoch to inspect scored heatmaps and performance output."
          title="No scoring output"
        />
      )}
    </>
  );
};

const getThreadNoScoreReason = (params: {
  activeThread?: StudyRunCaseDashboardThreadDetailDto | null;
  activeThreadVerdict?: StudyRunCaseDashboardThreadVerdictDto | null;
}): string | null => {
  const { activeThread, activeThreadVerdict } = params;
  if (!activeThread || !activeThreadVerdict) return 'Select a mission thread.';
  if (activeThread.summary.threadScore !== null && activeThread.summary.threadScore !== undefined) return null;
  if (activeThreadVerdict.score !== null && activeThreadVerdict.score !== undefined) return null;

  const hasStages = activeThread.stageHeatmap.length > 0;
  if (!hasStages) {
    return 'No stages found for this thread.';
  }

  const taskCount = activeThread.stageHeatmap.reduce((sum, row) => sum + row.taskCount, 0);
  if (taskCount === 0) {
    return 'No tasks found. Add P3 tasks, then run again.';
  }

  return 'Latest output has no scored nodes for this thread. Check mission scope, MoP links, and task scoring readiness.';
};
